import { ApiInboxSummaryResSchema } from "@bridge-applications/pb-types";
import { useCallback, useEffect, useRef, useState } from "react";
import { authenticatedApiCall } from "../helpers/api";

export function useInboxSummary(enabled: boolean) {
    const [notificationCount, setNotificationCount] = useState<string | null>(
        null,
    );
    const [refreshAt, setRefreshAt] = useState<number | null>(null);
    const requestRef = useRef<AbortController | null>(null);

    const refreshSummary = useCallback(async () => {
        if (!enabled) return;
        requestRef.current?.abort();
        const controller = new AbortController();
        requestRef.current = controller;
        try {
            const { body } = await authenticatedApiCall(
                "/api/v2/inbox/summary",
                ApiInboxSummaryResSchema,
                { signal: controller.signal },
            );
            if (requestRef.current !== controller) return;
            if (body.status === "ERROR") throw new Error(body.code);
            setNotificationCount(body.notificationCount);
            setRefreshAt(
                body.nextChangeAt === null
                    ? null
                    : Date.now() +
                          Math.max(
                              0,
                              Date.parse(body.nextChangeAt) -
                                  Date.parse(body.observedAt),
                          ) +
                          100,
            );
        } catch {
            // Keep the last server count and retry; a summary failure must not
            // turn a completed read or reward claim into a failed operation.
            if (
                requestRef.current === controller &&
                !controller.signal.aborted
            ) {
                setRefreshAt(Date.now() + 30_000);
            }
        } finally {
            if (requestRef.current === controller) requestRef.current = null;
        }
    }, [enabled]);

    useEffect(() => {
        if (!enabled) {
            setNotificationCount(null);
            setRefreshAt(null);
        }
        return () => {
            requestRef.current?.abort();
            requestRef.current = null;
        };
    }, [enabled]);

    useEffect(() => {
        if (!enabled || refreshAt === null) return;
        const timer = window.setTimeout(
            () => void refreshSummary(),
            Math.min(Math.max(0, refreshAt - Date.now()), 2_147_483_647),
        );
        return () => window.clearTimeout(timer);
    }, [enabled, refreshAt, refreshSummary]);

    return { notificationCount, refreshSummary };
}
